import React, {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useState
} from "react";
import {useDispatch} from "react-redux";
import {fetchSavedInvestigations} from "store/features/savedInvestigationsSlice";
import {
  DEFAULT_PAGE,
  REDUCED_RESULTS_PER_PAGE,
  SAVED_INVESTIGATIONS_FILTER_SORT_DEFAULT
} from "models/options";

const SavedInvestigationsContext = createContext({
  investigations: [],
  totalInvestigationsCount: 0,
  page: DEFAULT_PAGE,
  pageCount: REDUCED_RESULTS_PER_PAGE,
  order: SAVED_INVESTIGATIONS_FILTER_SORT_DEFAULT.order,
  orderBy: SAVED_INVESTIGATIONS_FILTER_SORT_DEFAULT.orderBy,
  isLoading: false,
  setPage: () => {},
  setPageCount: () => {},
  dispatchFilterSettings: () => {},
  handleSearchTextChange: () => {}
});

const SavedInvestigationsProvider = ({children}) => {
  const dispatch = useDispatch();

  const initialFilters = SAVED_INVESTIGATIONS_FILTER_SORT_DEFAULT;

  const [searchQuery, setSearchQuery] = useState("");

  const [filterSettings, dispatchFilterSettings] = useReducer(
    (state, action) => ({...state, ...action}),
    {
      order: initialFilters.order,
      orderBy: initialFilters.orderBy,
      page: DEFAULT_PAGE,
      pageCount: REDUCED_RESULTS_PER_PAGE,
      searchText: ""
    }
  );

  /**
   * Serialize settings to use as useEffect dependency.
   * Protects against issues when non-primitive values in state change.
   */
  const settingsString = JSON.stringify(filterSettings);

  const performInvestigationsFetch = useCallback(() => {
    dispatch(
      fetchSavedInvestigations({...filterSettings, searchText: searchQuery})
    );
    // Don't need exhaustive deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, settingsString, searchQuery]);

  useEffect(() => {
    performInvestigationsFetch();
  }, [performInvestigationsFetch, settingsString, searchQuery]);

  const setPage = (page) => dispatchFilterSettings({page});

  const handleSearchTextChange = (searchText) => {
    setPage(DEFAULT_PAGE);
    setSearchQuery(searchText);
  };

  const setPageCount = (results) =>
    dispatchFilterSettings({pageCount: results});

  return (
    <SavedInvestigationsContext.Provider
      value={{
        page: filterSettings.page,
        pageCount: filterSettings.pageCount,
        filterSettings,
        setPage,
        setPageCount,
        handleSearchTextChange,
        dispatchFilterSettings
      }}>
      {children}
    </SavedInvestigationsContext.Provider>
  );
};

export {SavedInvestigationsContext, SavedInvestigationsProvider};
